import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  Users,
  Vote,
  Building2,
} from 'lucide-react';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string; size?: number | string }>;
  badge?: number;
  description?: string;
}

interface MobileBottomNavProps {
  onSearchOpen?: () => void;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ onSearchOpen }) => {
  const location = useLocation();
  // Remove scroll-based visibility - always show the nav
  const navVisible = true;

  const navItems: NavItem[] = [
    {
      path: '/',
      label: 'Inicio',
      icon: Home,
      description: 'Panel principal',
    },
    {
      path: '/diputados',
      label: 'Diputados',
      icon: Users,
      description: 'Representantes',
    },
    {
      path: '/votaciones',
      label: 'Votaciones',
      icon: Vote,
      description: 'Historial de votos',
    },
    {
      path: '/partidos',
      label: 'Partidos',
      icon: Building2,
      description: 'Grupos políticos',
    },
  ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const triggerHaptic = (intensity: number = 10) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(intensity);
    }
  };

  // Remove all scroll and touch event listeners since nav is always visible

  const TabButton: React.FC<{
    item: NavItem;
    isActive: boolean;
    compact?: boolean;
  }> = ({ item, isActive: active, compact = false }) => {
    const Icon = item.icon;
    const [isPressed, setIsPressed] = useState(false);

    return (
      <Link
        to={item.path}
        onPointerDown={() => {
          setIsPressed(true);
          triggerHaptic(8);
        }}
        onPointerUp={() => {
          setIsPressed(false);
        }}
        onPointerLeave={() => setIsPressed(false)}
        className={`relative flex ${compact ? 'flex-col' : 'flex-row'} items-center ${compact ? 'justify-center' : 'justify-start'} ${compact ? 'py-2 px-1' : 'py-3 px-4'} ${compact ? 'flex-1' : 'w-full'} group transition-all duration-200`}
        style={{
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
        }}
        aria-label={item.label}
        aria-current={active ? 'page' : undefined}
      >
        {!compact && active && (
          <motion.div
            layoutId="activeIndicator"
            className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full"
            initial={false}
            transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30,
            }}
          />
        )}

        <motion.div
          animate={{
            scale: isPressed ? 0.9 : active ? 1.1 : 1,
            x: !compact && active ? 4 : 0,
          }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 17,
          }}
          className={`flex ${compact ? 'flex-col' : 'flex-row'} items-center ${compact ? 'space-y-1' : 'space-x-3'}`}
        >
          <div className="relative">
            <Icon
              size={compact ? 20 : 24}
              className={`transition-all duration-200 ${
                active
                  ? 'text-white drop-shadow-[0_2px_8px_rgba(255,255,255,0.3)]'
                  : 'text-blue-100/80 group-hover:text-white'
              }`}
            />

            {item.badge && item.badge > 0 && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] rounded-full min-w-[16px] h-[16px] flex items-center justify-center font-bold shadow-lg"
              >
                {item.badge > 99 ? '99+' : item.badge}
              </motion.div>
            )}
          </div>

          <div className={`${compact ? 'text-center' : 'text-left'}`}>
            <motion.span
              className={`${compact ? 'text-[10px]' : 'text-sm'} font-semibold transition-colors duration-200 ${
                active
                  ? 'text-white'
                  : 'text-blue-100/90 group-hover:text-white'
              }`}
            >
              {item.label}
            </motion.span>
            {!compact && item.description && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: active ? 1 : 0.7, height: 'auto' }}
                className={`text-xs mt-0.5 transition-opacity duration-200 ${
                  active ? 'text-blue-100' : 'text-blue-100/60'
                }`}
              >
                {item.description}
              </motion.p>
            )}
          </div>
        </motion.div>
      </Link>
    );
  };


  return (
    <>
      <motion.nav
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30,
        }}
        className="fixed bottom-0 left-0 right-0 z-50"
      >
        <div
          className="relative bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 shadow-2xl"
          style={{
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            boxShadow: '0 -4px 24px rgba(0, 51, 160, 0.3), 0 -2px 12px rgba(0, 51, 160, 0.2)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />

          <div className="relative flex items-center justify-around px-2 py-2">
            {navItems.map((item, index) => (
              <React.Fragment key={item.path}>
                <TabButton
                  item={item}
                  isActive={isActive(item.path)}
                  compact={true}
                />
                {index < navItems.length - 1 && (
                  <div className="w-px h-8 bg-white/20" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </motion.nav>


      <div className="h-20" style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }} />
    </>
  );
};